import { Typography, Chip, Box, TextField } from "@mui/material";
import { Autocomplete } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { CategoryOption } from "Types/CategoryOption";
import { Resources, useResource } from "Translations/Resources";

type Props = {
  categories: Array<CategoryOption>;
  name: string;
  label: string;
  disabled?: boolean;
  onChange: (value: Array<string>) => void;
  value: Array<string>;
  errorMessage?: string;
};

const COMPONENT_RESOURCES = Resources.Components.CategoryAutocomplete;

export const CategoryAutocomplete = ({
  categories,
  name,
  label,
  disabled,
  onChange,
  value: propsValue,
  errorMessage,
}: Props) => {
  const { t } = useResource();

  return (
    <div>
      {label && (
        <Box display="flex" alignItems="center">
          <Typography component="label" htmlFor={name}>
            {label}
          </Typography>
        </Box>
      )}
      <Autocomplete
        multiple
        options={categories}
        getOptionLabel={({ label }) => label}
        value={categories.filter(({ value }) => propsValue?.includes(value))}
        onChange={(_, newValues) => {
          onChange(newValues.map(({ value }) => value));
        }}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={
              propsValue?.length ? "" : t(COMPONENT_RESOURCES.Placeholder)
            }
            fullWidth
            sx={{
              mt: 1,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: errorMessage ? "error.main" : "divider",
                borderRadius: 1,
              },
            }}
            error={!!errorMessage}
            helperText={errorMessage || " "}
          />
        )}
        renderValue={(value, getTagProps) =>
          value.map((category, index) => (
            <Chip
              {...getTagProps({ index })}
              key={category.value}
              label={category.label}
              deleteIcon={<CloseIcon />}
              onDelete={() => {
                const newValues = value
                  .filter((_, i) => i !== index)
                  .map((category) => category.value);
                onChange(newValues);
              }}
            />
          ))
        }
        renderOption={({ key, ...restProps }, { label }) => (
          <Box component="li" key={key} {...restProps}>
            <Typography fontWeight={400}>{label}</Typography>
          </Box>
        )}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        fullWidth
      />
    </div>
  );
};
